import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Spinner } from "@fluentui/react-components";
import {
    vorgangService,
    type NoteRefArt,
    type VerknuepftesTodoDto,
    type VorgangDto,
} from "../services/vorgangService";
import { documentService } from "../services/documentService";
import { vorgangAustauschService, type AustauschDto } from "../services/vorgangAustauschService";
import { bezugKandidaten } from "../components/notizbloecke/bezugKandidaten";
import { displayNameOf } from "../components/telephony/telephonyUtils";
import { labelForUserKey } from "../services/userService";
import { kundeName } from "../utils/vorgangScope";
import { useVorgangStrang, type GemeinsameStrangProps } from "../hooks/useVorgangStrang";
import { betreffOhneArt } from "../data/vorgangArt";
import { slotsFromDto, vorgangBodyAusPatch, type CascadeResult } from "../components/vorgang/detail/linkSlots";
import { resolveErstellerId, type BeteiligtenFeld } from "../components/vorgang/detail/personenZeile";
import { TodoAnlegenKasten } from "../components/vorgang/v3/TodoAnlegenKasten";
import { TodoVerknuepfenDialog } from "../components/vorgang/v3/TodoVerknuepfenDialog";
import { TodoVerschiebenDialog } from "../components/vorgang/v4/TodoVerschiebenDialog";
import { VorgangAlsTodoVerschiebenDialog } from "../components/vorgang/v4/VorgangAlsTodoVerschiebenDialog";
import { VorgangTeilenDialog } from "../components/vorgang/v4/VorgangTeilenDialog";
import { VorgangZusammenfuehrenMitDialog } from "../components/vorgang/v4/VorgangZusammenfuehrenMitDialog";
import { VerknuepftesTodoKarte } from "../components/vorgang/v3/VerknuepftesTodoKarte";
import {
    leererTodoEntwurf,
    todoAnlagePayload,
    todoEntwurfGueltig,
    type TodoEntwurf,
} from "../components/vorgang/v3/todoEntwurf";
import { ZusammenfassungDialog } from "../components/vorgang/v3/ZusammenfassungDialog";
import { ZusammenfassungKarte } from "../components/vorgang/v3/ZusammenfassungKarte";
import { zusammenfassungFaellig } from "../components/vorgang/v3/zusammenfassung";
import { IcoPlus } from "../components/vorgang/v3/vg3Icons";
import { dokumenteAus, verlaufFuerTodo, type VerlaufEintrag } from "../components/vorgang/v3/verlauf";
import { planeTodoAnlage } from "../components/vorgang/v3/todoDarstellung";
import {
    laufendeNotiz,
    notizUebernahmeWarnung,
    ohneLaufendeNotiz,
    uebernimmNotizen,
    umhaengeNotizAnTodo,
} from "../components/vorgang/v4/erstesTodoNotizen";
import {
    kommentareJeZiel,
    zielSchluessel,
    type KommentarZiel,
} from "../components/notizbloecke/kommentarModell";
import { zeigeVorgangsStrang } from "../components/vorgang/v4/vorgangAlsStrang";
import { VorgangKopfV4 } from "../components/vorgang/v4/VorgangKopfV4";
import { ArbeitsstrangV4 } from "../components/vorgang/v4/ArbeitsstrangV4";
import { ArchivAbschnitt } from "../components/vorgang/v4/ArchivAbschnitt";
import { TodoKarteV4 } from "../components/vorgang/v4/TodoKarteV4";
import { useSprungmarke } from "../components/vorgang/v4/useSprungmarke";
import {
    aufAkkordeon,
    ladeZuletztGeoeffnet,
    merkeZuletztGeoeffnet,
    startOffen,
    umschalteOffen,
} from "../components/vorgang/v4/todoOffen";
import { KopfKontaktKnoepfe } from "../components/vorgang/v4/mobil/KopfKontaktKnoepfe";
import {
    PlusHinweis,
    PlusKnopf,
    PlusSheet,
    type PlusAnfrage,
    type PlusNotiz,
    type PlusTodoAnlage,
} from "../components/vorgang/v4/mobil/PlusMobil";
import { textAlsNotizHtml } from "../components/vorgang/v4/mobil/notizText";
import { baueKommentarInhalt } from "../components/notizbloecke/kommentarInhalt";
import "../components/vorgang/v4/mobil/vorgangMobil.css";
import { useResponsive } from "../hooks/useResponsive";
import { istTodoErledigt } from "../components/vorgang/detail/todoRowActions";
import { strangZahlen, strangZahlenText } from "../components/vorgang/v4/strangZaehlung";
import { VorgangAlsTodoKarteV4 } from "../components/vorgang/v4/VorgangAlsTodoKarteV4";
import type { AktionsKnoepfeOptionen } from "../components/vorgang/v4/aktionsKnoepfe";
import { DateiDropZoneV4 } from "../components/vorgang/v4/DateiDropZoneV4";
import {
    useVorgangEntwuerfe,
    VorgangEntwuerfeV4,
    verteileEntwuerfe,
} from "../components/vorgang/v4/VorgangEntwuerfeV4";
import "../components/redesign/contactRedesign.css";
import "../components/vorgang/v3/vorgangV3.css";
import "../components/vorgang/v4/vorgangV4.css";
import { bestaetigen, hinweisen } from "../components/redesign/bestaetigen";

/**
 * Vorgang-Detail V4 — die Notizansicht und seit dem 14.09.2026 die NORMALE
 * Ansicht: `/vorgaenge/:id` zeigt sie (PO 13./14.09.2026, letzter der fünf
 * Punkte). `/vorgaenge/v4/:id` bleibt daneben bestehen — der Pfad steht in
 * Lesezeichen und verschickten Links. Seit dem 20.09.2026 ist sie die EINZIGE
 * Ansicht: V2, V3, V5 und V6 sind entfernt, ihre Pfade leiten hierher.
 *
 * ══ WAS SIE ANDERS MACHT ALS V3 ════════════════════════════════════════════
 * V3 stellt drei Dinge untereinander: Notizfläche, Aktionen, Verlauf. Wer
 * wissen will, was passiert ist, liest zweimal — einmal die Notizen, einmal
 * die Chronik daneben.
 *
 * V4 legt beides in EINEN Strom (Zeichnung 10.09.2026): Mails, Anrufe,
 * Dokumente und — seit PO 14.09.2026 — Termine mit Notiz-Seite stehen als
 * Karten zwischen den Notizen, an der Stelle, an der sie passiert sind. Der
 * Abschnitt „Verlauf“ entfällt, ebenso die Anhang-Leiste.
 *
 * Ein Ereignis wird dabei NICHT zur Notiz: es wird nur mitgerendert. In der
 * Datenbank bleibt die Mail eine Mail und die Termin-Notiz die Seite am
 * Kalender-Sidecar — nichts wird kopiert, nichts veraltet.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ DAS ARCHIV: EINE ENTSCHEIDUNG UND IHRE RÜCKNAHME ═══════════════════════
 * Am 10.09.2026 entfiel mit dem Verlauf auch das Archiv; die vollständigen
 * Listen lagen hinter „Alle Mails“ und „Alle Dokumente“ in der Aktionsleiste.
 *
 * Am 11.09.2026 hat der PO das zurückgewiesen: „Das muss unten runter, unter
 * alle To-dos, und da ist alles von allen To-dos drin.“ (17:43) — das Archiv
 * steht seither als {@link ArchivAbschnitt} am Seitenende und trägt Mails,
 * Anrufe, Dokumente UND Notizen des ganzen Vorgangs, mit einer Suche, die nur
 * hier sucht. Die beiden Nachschlage-Knöpfe der Aktionsleiste sind damit
 * entfallen; drei Wege zur selben Liste wären zwei zu viel.
 *
 * Beide Fassungen stehen hier, weil die zweite ohne die erste nicht zu
 * verstehen ist: Das Archiv ist NICHT der Verlauf. Der Verlauf als eigene
 * Spalte bleibt aufgelöst — das Archiv ist die Suchfläche darüber.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Fachlogik — Laden, Speichern, To-do-Anlage, Notizblöcke,
 * Zusammenfassung — kommt aus den Modulen, die V3 mitgebracht hat (Ordner
 * `components/vorgang/v3/`, der deshalb weiterlebt); V4 ist eine andere
 * Anordnung, kein zweites Fachmodell.
 */

/** Stabile leere Liste — sonst bekäme jede To-do-Karte bei jedem Rendern eine neue. */
const KEINE_ENTWUERFE: ReadonlyArray<never> = [];
/** Die laufende Notiz zeigt keinen Verlauf — stabil, damit nichts neu gebaut wird. */
const KEINE_EINTRAEGE: VerlaufEintrag[] = [];

const VorgangDetailPageV4: React.FC = () => {
    const { id: idStr } = useParams();
    const id = Number(idStr);
    const navigate = useNavigate();

    const [titel, setTitel] = useState("");
    const [beschreibung, setBeschreibung] = useState("");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savedFlash, setSavedFlash] = useState(false);

    const [zusDialog, setZusDialog] = useState<"abschluss" | "aendern" | null>(null);
    /** Der zuletzt GESEHENE Status; `null` nur beim ersten Laden. */
    const statusVorherRef = React.useRef<string | null>(null);

    const [neuesTodo, setNeuesTodo] = useState<TodoEntwurf | null>(null);
    /**
     * Der Name des ERSTEN To-dos, das beim Anlegen aus dem bisherigen Vorgang
     * entsteht (PO 28.09.2026). `null` = unverändert, dann gilt der Vorschlag
     * (der Vorgangsbetreff) — so folgt das Feld einer Betreffänderung, solange
     * niemand hineingetippt hat.
     */
    const [erstesTodoTitel, setErstesTodoTitel] = useState<string | null>(null);
    const [verknuepfte, setVerknuepfte] = useState<VerknuepftesTodoDto[]>([]);
    /** Die Austausche des Vorgangs und seiner To-dos (T-302) — Quelle aller A-Knöpfe. */
    const [austausch, setAustausch] = useState<AustauschDto[]>([]);
    const [verknuepfenOffen, setVerknuepfenOffen] = useState(false);
    /* Die beiden ⋯-Wege des Vorgangskopfs (PO 21.09.2026). Getrennte Zustände
       statt eines Enums: die Dialoge haben nichts miteinander zu tun, und ein
       gemeinsamer Zustand lüde dazu ein, sie irgendwann zu verschmelzen. */
    const [todoVerschieben, setTodoVerschieben] = useState(false);
    const [vorgangVerschieben, setVorgangVerschieben] = useState(false);
    /** „Mit anderem Vorgang zusammenführen…" / „Vorgang teilen…" (PO 28.09.2026). */
    const [zusammenfuehrenMit, setZusammenfuehrenMit] = useState(false);
    const [teilen, setTeilen] = useState(false);
    const [todoBusy, setTodoBusy] = useState(false);
    /** Eintrag, der dem NÄCHSTEN neu angelegten To-do zugewiesen wird. */
    const [todoZielEintrag, setTodoZielEintrag] = useState<VerlaufEintrag | null>(null);
    /** Notizblock, der dem NÄCHSTEN neu angelegten To-do zugewiesen wird. */

    const flashSaved = useCallback(() => {
        setSaveError(null);
        setSavedFlash(true);
        window.setTimeout(() => setSavedFlash(false), 1500);
    }, []);

    const ladeVerknuepfte = useCallback(async () => {
        try {
            setVerknuepfte(await vorgangService.verknuepfteTodos(id));
        } catch (err) {
            // Kein harter Fehler: verknüpfte To-dos sind eine Ergänzung.
            console.error("[VorgangDetailV4] Verknüpfte To-dos laden fehlgeschlagen", err);
        }
    }, [id]);

    /*
     * Mit dem übrigen Vorgang geladen (`zusatzLaden`) — und damit auch bei
     * jedem `vorgang:dirty`, das der Server nach einem Austausch schickt.
     * Kein harter Fehler: der Austausch ist eine Ergänzung, ohne ihn stehen
     * die A-Knöpfe grau da.
     */
    const ladeAustausch = useCallback(async () => {
        try {
            setAustausch(await vorgangAustauschService.liste(id));
        } catch (err) {
            console.error("[VorgangDetailV4] Austausch laden fehlgeschlagen", err);
        }
    }, [id]);

    const strang = useVorgangStrang(id, {
        onVorgangGeladen: (v) => {
            setTitel(betreffOhneArt(v.vorgangsart, v.titel));
            setBeschreibung(v.beschreibung ?? "");
        },
        zusatzLaden: async () => {
            await Promise.all([ladeVerknuepfte(), ladeAustausch()]);
        },
        onGespeichert: () => flashSaved(),
        // „Neues To-do…" im Rechtsklick-Menü: Eintrag merken, Anlege-Kasten öffnen.
        // Den Vorgang reicht der Hook mit; `currentUserId` stammt aus dem
        // Hook-Ergebnis unten — der Callback läuft erst beim Klick und wird
        // über `optsRef` frisch gehalten.
        onNeuesTodoFuer: (eintrag, v) => {
            setTodoZielEintrag(eintrag);
            setNeuesTodo(
                leererTodoEntwurf(slotsFromDto(v), {
                    hauptverantwortlich: v.hauptverantwortlich,
                    bearbeiter: v.bearbeiter,
                    zuInformierende: v.zuInformierende,
                    currentUserId,
                }),
            );
        },
    });
    const {
        vorgang, setVorgang, loading, verlaufGeladen, error, canEdit, currentUserId, users,
        notizen, notizenAnzeige, mails, mailGruppen,
        todos, eintraege, eigeneEintraege, zugewieseneMessageIds, vorhandeneDokumentIds,
        bezugRisiken, bezugVertraege, gemeinsameProps,
        ladeVorgang, ladeAlles,
        zeigeHinweis,
        handlers, laufendeHandlers, speichereEintragZuweisung,
    } = strang;

    /**
     * Mail-Entwürfe des Vorgangs (T-231): am To-do in dessen Karte, am Vorgang
     * im Vorgangs-Strang (ohne To-dos) bzw. in einer eigenen Karte (mit To-dos).
     */
    const entwuerfe = useVorgangEntwuerfe(id);
    const entwurfsOrte = React.useMemo(
        () => verteileEntwuerfe(entwuerfe, todos.map((t) => t.id)),
        [entwuerfe, todos],
    );

    /**
     * Nach der Zusammenfassung fragen, sobald der Vorgang erledigt IST
     * (PO 09.09.2026) — am Status, nicht am Klick: er wird serverseitig
     * abgeleitet und wechselt auf zwei Wegen.
     */
    useEffect(() => {
        const jetzt = vorgang?.status ?? null;
        if (jetzt === null) return;
        if (zusammenfassungFaellig(statusVorherRef.current, jetzt, vorgang?.zusammenfassung)) {
            setZusDialog("abschluss");
        }
        statusVorherRef.current = jetzt;
    }, [vorgang?.status, vorgang?.zusammenfassung]);

    /* ── Schreiben ───────────────────────────────────────────────────────── */

    const patch = useCallback(
        async (data: Record<string, unknown>) => {
            try {
                const updated = await vorgangService.updateVorgang(id, data as Partial<VorgangDto>);
                setVorgang(updated);
                flashSaved();
            } catch (err: any) {
                console.error("[VorgangDetailV4] Speichern fehlgeschlagen", err);
                setSaveError(err?.message ?? "Speichern fehlgeschlagen.");
            }
        },
        [id, flashSaved],
    );

    const patchBeteiligte = useCallback(
        async (feld: BeteiligtenFeld, next: string[]) => {
            try {
                // Bearbeiter/Hauptverantwortliche sind Autorisierungsfelder und
                // laufen über den Spezial-Endpunkt. „Zu informieren“ ist kein
                // Zugriffsrecht und wird von ihm bewusst ignoriert.
                const updated = feld === "zuInformierende"
                    ? await vorgangService.updateVorgang(id, { zuInformierende: next })
                    : await vorgangService.updateVorgangBeteiligte(id, { [feld]: next } as any);
                setVorgang(updated);
                flashSaved();
            } catch (err: any) {
                console.error("[VorgangDetailV4] Beteiligte speichern fehlgeschlagen", err);
                setSaveError(err?.message ?? "Speichern fehlgeschlagen.");
            }
        },
        [id, flashSaved],
    );

    /* ── Ableitungen ─────────────────────────────────────────────────────── */

    const hatTodos = todos.length > 0;

    /**
     * DIE LAUFENDE NOTIZ (User 01.11.2026: „Ein Vorgang mit To-dos hat auch
     * einen Notiz-Bereich, nur ein Notiz-Bereich, nicht Verlauf.").
     *
     * Mit To-dos steht oben im Vorgang ein Notizfeld OHNE Verlauf; Mails,
     * Dokumente und Anrufe bleiben an den To-dos. Was dort geschrieben wird,
     * trägt `laufend` und bleibt am Vorgang — die Übernahme ins erste To-do
     * lässt es stehen (`notizenAmVorgang`, serverseitig `STRANG_TABELLEN`).
     * Der Altbestand ohne Kennzeichen wandert weiter wie bisher.
     */
    const laufende = useMemo(() => laufendeNotiz(notizenAnzeige), [notizenAnzeige]);
    const ohneLaufende = useMemo(() => ohneLaufendeNotiz(notizenAnzeige), [notizenAnzeige]);

    /**
     * Nur Id und Betreff — das Archiv schreibt damit an jede Zeile, aus welchem
     * To-do sie stammt. Eigener Memo, damit der Abschnitt nicht bei jedem
     * Rendern eine neue Liste sieht und seine Zeilen neu baut.
     *
     * Die Vorgangskarte steht hier bewusst NICHT drin: sie ist kein To-do, und
     * `archivKopfText` zählt „aus N To-dos“. Ein Mitzählen verfälschte die
     * Auskunft über den Datenbestand.
     */
    const archivTodos = useMemo(() => todos.map((t) => ({ id: t.id, titel: t.titel })), [todos]);

    /**
     * Jedes To-do mit seiner Zählung („2 Notizen · 5 im Verlauf") — Grundlage
     * für den Dialog „To-do zusammenführen…" (PO 16.09.2026). Er muss zeigen,
     * was am Gegenüber hängt, BEVOR man beides verschmilzt.
     *
     * Hier und nicht in der Karte: die Zählung braucht den Verlauf ALLER
     * To-dos; eine Karte kennt nur ihren eigenen Strang.
     */
    const todoZaehlungen = useMemo(
        () =>
            todos.map((t) => ({
                todo: t,
                zaehlung: strangZahlenText(
                    strangZahlen({
                        eintraege: verlaufFuerTodo(eintraege, t.id),
                        alleNotizen: notizenAnzeige,
                        todoId: t.id,
                    }),
                ),
            })),
        [todos, eintraege, notizenAnzeige],
    );

    /**
     * DER VORGANG ALS ERSTE TO-DO-KARTE (PO 13./14.09.2026).
     *
     * Hat ein Vorgang To-dos UND Eigenes (`todoId == null`), stand das bisher
     * nirgends: der Vorgangsstrom hing an `{!hatTodos && …}`. Betroffen ist der
     * Normalfall im Bestand — in der Altansicht V2 (bis 14.09.2026 Standard)
     * entstanden To-dos ohne die Übernahme aus `planeTodoAnlage`. Begründung und Regel:
     * `vorgangAlsStrang.ts`.
     */
    const vorgangsKarte = zeigeVorgangsStrang({ hatTodos, eigeneEintraege, notizen: notizenAnzeige });

    /**
     * … UND SIE HOLT SICH SELBST AB (PO 16.09.2026).
     *
     * „der vorgang bei dem todos hinzugefügt werden wird das erste todo, daher
     * auch ein als erledigt markieren." Die Sonderkarte hat bewusst keinen
     * Erledigen-Knopf: mit To-dos leitet der Server den Status aus ihnen ab.
     * Statt dort eine zweite Wahrheit einzubauen, wird der Strang zu einem
     * ECHTEN ersten To-do — dann kommen Erledigen-Knopf und eigene Rollen von
     * selbst.
     *
     * Das Umhängen läuft serverseitig in einer Transaktion
     * (`todo/vorgang/:id/uebernahme`). Einmal je Seitenaufruf, nur mit
     * Schreibrecht; wer bloß liest, sieht weiter die Karte — sie ist der
     * Rückfallweg und bleibt erhalten, damit die Einträge in keinem Fall
     * unerreichbar werden.
     */
    const uebernahmeRef = React.useRef(false);
    useEffect(() => {
        if (!vorgangsKarte || !canEdit || uebernahmeRef.current || todoBusy) return;
        uebernahmeRef.current = true;
        void (async () => {
            try {
                const ergebnis = await vorgangService.uebernimmVorgangsstrang(id);
                if (ergebnis.angelegt) {
                    await ladeAlles();
                    flashSaved();
                }
            } catch (err) {
                // Kein Abbruch: Die Karte bleibt stehen und zeigt alles wie
                // bisher — nur eben ohne Erledigen-Knopf.
                console.error("[VorgangDetailV4] Übernahme des Vorgangsstrangs fehlgeschlagen", err);
            }
        })();
    }, [vorgangsKarte, canEdit, todoBusy, id, ladeAlles, flashSaved]);

    /*
     * DER BETREFF STEHT IM KOPF — IMMER (PO 18.09.2026).
     *
     * Vom 13. bis 18.09.2026 stand er genau einmal auf der Seite: eine Weiche
     * (`betreffTraeger`) gab ihn samt Untertitel an den Kopf, die Vorgangskarte
     * oder das erste To-do. Der Zettel vom 18.09.2026 dreht das um — „Vorgang
     * mit To-dos hat Überschrift und Untertitel; erstes To-do hat dann
     * dieselbe Überschrift und den Verlauf des bisherigen Vorgangs". Die
     * Dopplung ist gewollt: oben steht, worum der Vorgang geht, darunter,
     * woran gerade gearbeitet wird. Die Weiche ist damit ersatzlos entfallen.
     */

    /**
     * Jüngste Aktivität — für „zuletzt bearbeitet“ im Kopf. Notizen UND
     * Ereignisse: eine Akte gilt als angefasst, sobald irgendetwas an ihr
     * passiert ist, nicht nur wenn jemand geschrieben hat.
     */
    const zuletzt = useMemo(() => {
        let ms = 0;
        for (const n of notizen) {
            for (const iso of [n.timestamp, n.updatedAt]) {
                const t = iso ? new Date(iso).getTime() : 0;
                if (!Number.isNaN(t) && t > ms) ms = t;
            }
        }
        for (const e of eintraege) {
            const t = e.at.getTime();
            if (t > ms) ms = t;
        }
        return ms > 0 ? new Date(ms) : null;
    }, [notizen, eintraege]);

    const ersteller = useMemo(() => {
        if (!vorgang) return null;
        const uid = resolveErstellerId(vorgang);
        return uid ? labelForUserKey(uid, users) : null;
    }, [vorgang, users]);

    /* ── Zuordnung eines Notizblocks an ein To-do ────────────────────────── */
    /*
     * HIER STAND DAS UMHÄNGEN EINES NOTIZBLOCKS (bis 20.09.2026).
     *
     * Der Rechtsklick auf eine Notiz bot „→ zu To-do X", „→ zurück an den
     * Vorgang" und „→ neues To-do…". Mit dem EINEN Notizfeld gibt es keine
     * einzeln ansprechbaren Blöcke mehr, und der PO hat den Punkt gestrichen:
     * „nein, Text kann man ja kopieren."
     *
     * Der Serverweg (`umhaengeNotizAnTodo`) bleibt — die automatische Mitnahme
     * der Vorgangsnotizen beim ersten To-do (`uebernimmVorgangsstrang`) läuft
     * darüber.
     */

    /* ── To-do anlegen ───────────────────────────────────────────────────── */

    const todoAnlegen = useCallback(async () => {
        if (!neuesTodo || todoBusy || !vorgang || !todoEntwurfGueltig(neuesTodo)) return;
        // KEINE zweite Übernahme, während hier eine läuft (Fehler 17.09.2026):
        // `planeTodoAnlage` legt das erste To-do selbst an und hängt danach um.
        // Zwischen beiden Schritten steht die Seite kurz auf „hat To-dos UND
        // Eigenes" — genau die Lage, die den Übernahme-Effekt auslöst. Er legte
        // dann ein ZWEITES To-do mit dem Vorgangsbetreff an, und der Vorgang
        // stand doppelt da: einmal als erstes, einmal als letztes To-do.
        uebernahmeRef.current = true;
        setTodoBusy(true);
        try {
            const ctx = {
                vorgangId: vorgang.id,
                persoenlich: vorgang.persoenlich === true,
                currentUserId,
            };

            // DER VORGANG WIRD ZUM ERSTEN TO-DO (Zeichnung PO 13.09.2026).
            //
            // Sobald es To-dos gibt, zeigt die Seite den Vorgangsstrom nicht
            // mehr — der erste Übergang vom flachen Vorgang in To-dos würde
            // den schon vorhandenen Verlauf also unsichtbar machen. Deshalb
            // entsteht vor dem angeforderten To-do ein erstes, das den Vorgang
            // fortsetzt: mit SEINEM Betreff, und es übernimmt alles bisher
            // direkt am Vorgang Hängende.
            //
            // HERKUNFT: bis zum 13.09.2026 hieß dieses erste To-do
            // „<Betreff> - Vorarbeiten“. Im Bestand steht der Zusatz noch —
            // umbenannt wird nichts, das wäre eine stille Datenänderung.
            // Der Name kommt seit dem 28.09.2026 aus dem Anlege-Kasten — der
            // Anwender kann ihn dort gleich mit ändern (`erstesTodoTitel`).
            const vorschlagErstesTodo = titel.trim() || betreffOhneArt(vorgang.vorgangsart, vorgang.titel);
            const plan = planeTodoAnlage({
                vorgangTitel: (erstesTodoTitel ?? "").trim() || vorschlagErstesTodo,
                todos: vorgang.todos ?? [],
                eintraege,
            });
            let notizWarnung: string | null = null;
            if (plan.erstesTodoTitel) {
                const erstes = await vorgangService.createTodo(
                    todoAnlagePayload(
                        {
                            ...leererTodoEntwurf(slotsFromDto(vorgang), {
                                hauptverantwortlich: vorgang.hauptverantwortlich,
                                bearbeiter: vorgang.bearbeiter,
                                zuInformierende: vorgang.zuInformierende,
                                currentUserId,
                            }),
                            titel: plan.erstesTodoTitel,
                        },
                        ctx,
                    ) as any,
                );
                for (const eintrag of plan.zuUebernehmendeEintraege) {
                    await speichereEintragZuweisung(eintrag, erstes.id);
                }
                // Die NOTIZEN wandern mit (Design-Stand 08.09.2026: „Sobald das
                // erste To-do entsteht, wird die bisherige Vorgangsnotiz zur
                // Notiz des ersten To-dos.“). Sie stecken NICHT in `eintraege`
                // — `baueVerlauf` liefert seit dem 08.09.2026 keine Notizen
                // mehr —, also braucht es diesen zweiten Handgriff.
                // `umhaengeNotizAnTodo` schickt AUSSCHLIESSLICH `todoId` —
                // nur so lässt das Backend auch die Notiz einer Kollegin
                // wandern (dort: `istNurUmhaengenAnTodo`). Ein zusätzliches
                // Feld machte daraus eine Bearbeitung, die nur dem Autor
                // offensteht, und der Block bliebe unsichtbar am Vorgang.
                const uebernahme = await uebernimmNotizen(notizen, erstes.id, umhaengeNotizAnTodo);
                // Kein Abbruch bei Fehlschlag: das erste To-do steht schon, das
                // angeforderte soll trotzdem entstehen. Aber verschwiegen wird
                // nichts — ein liegen gebliebener Block ist ab jetzt unsichtbar.
                notizWarnung = notizUebernahmeWarnung(uebernahme.fehlgeschlagen);
            }

            const angelegt = await vorgangService.createTodo(todoAnlagePayload(neuesTodo, ctx) as any);
            // „Neues To-do + zuweisen“: erst anlegen, dann den gemerkten
            // Eintrag umhängen. Schlägt das Zuweisen fehl, bleibt das To-do
            // bestehen — besser ein leeres To-do als ein verlorener Betreff.
            if (todoZielEintrag) {
                await speichereEintragZuweisung(todoZielEintrag, angelegt.id);
                setTodoZielEintrag(null);
            }
            setNeuesTodo(null);
            setErstesTodoTitel(null);
            setTodoAnlageImSheet(false);
            await ladeAlles();
            // Aus dem Plus angelegt: die Aktion läuft dort mit der neuen Id weiter.
            const weiter = nachTodoAnlageRef.current;
            nachTodoAnlageRef.current = null;
            weiter?.(angelegt.id, angelegt.titel || neuesTodo.titel || `To-do ${angelegt.id}`);
            if (notizWarnung) void hinweisen({ titel: "To-do angelegt", text: notizWarnung });
        } catch (err: any) {
            console.error("[VorgangDetailV4] To-do anlegen fehlgeschlagen", err);
            void hinweisen({ titel: "Kein To-do angelegt", text: err?.message ?? "To-do konnte nicht angelegt werden.", ton: "danger" });
        } finally {
            setTodoBusy(false);
        }
    }, [
        neuesTodo,
        todoBusy,
        vorgang,
        currentUserId,
        titel,
        erstesTodoTitel,
        eintraege,
        notizen,
        ladeAlles,
        todoZielEintrag,
        speichereEintragZuweisung,
    ]);

    /**
     * `?mail=<MailLink-Id>` öffnet diese Mail direkt (PO 28.09.2026: „Bei
     * Notification bei Mail zu einem Vorgang, drauf drücken dann öffnet sich
     * die Mail"). Die Mitteilung verlinkt so, statt auf `/emails?…`: dort
     * bräuchte man Zugriff aufs Postfach, und Vorgangs-Mails liegen meist in
     * einem geteilten (cbb@) — hier trägt der Vorgang das Recht.
     *
     * Gewartet wird, bis die Mails geladen sind; danach verschwindet der
     * Parameter, damit ein Neuladen die Mail nicht erneut öffnet.
     */
    const [suchParameter, setSuchParameter] = useSearchParams();
    const mailParameter = suchParameter.get("mail");
    useEffect(() => {
        if (!mailParameter) return;
        // In der ROHLISTE suchen: die Mitteilung kann auf eine ausgeblendete
        // Postfachkopie zeigen (T-11). Geöffnet wird ihr Vertreter, damit
        // Hervorhebung und Kommentarspalte zur sichtbaren Karte passen.
        const link = mails.find((m) => String(m.id) === mailParameter);
        if (!link) return;
        const vertreterId = mailGruppen.vertreterVon.get(link.id) ?? link.id;
        handlers.onOeffneMail(mails.find((m) => m.id === vertreterId) ?? link);
        setSuchParameter(
            (vorher) => {
                const naechste = new URLSearchParams(vorher);
                naechste.delete("mail");
                return naechste;
            },
            { replace: true },
        );
    }, [mailParameter, mails, mailGruppen, handlers, setSuchParameter]);

    /**
     * `?todo=<id>&notiz=<quelle>:<id>` (User 01.11.2026): Mitteilung,
     * Kontaktansicht und Kundentagebuch springen in das To-do und zur Notiz
     * bzw. zum Kommentar — Regeln in `components/vorgang/v4/sprungmarke.ts`.
     */
    const todoIds = useMemo(() => todos.map((t) => t.id), [todos]);

    /**
     * WELCHE TO-DOS OFFEN SIND, weiß seit dem Plan „Vorgangsdetail mobil"
     * (PR 1) die Seite, nicht mehr jede Karte für sich: Das Plus auf dem
     * Handy schlägt das offene To-do als Ziel vor. Am Desktop dürfen weiter
     * mehrere offen sein; was zuletzt aufgeklappt wurde, merkt sich der Tab
     * je Vorgang (`todoOffen.ts`).
     */
    const [offeneTodos, setOffeneTodos] = useState<ReadonlySet<number>>(() => new Set());
    useEffect(() => setOffeneTodos(new Set()), [id]);

    /** Das Plus auf dem Handy (Plan mobil, PR 3): offen für welches Ziel, und der Hinweis danach. */
    const [plusAnfrage, setPlusAnfrage] = useState<PlusAnfrage | null>(null);
    const [plusHinweis, setPlusHinweis] = useState<string | null>(null);
    const plusSchliessen = useCallback(() => setPlusAnfrage(null), []);
    const plusHinweisWeg = useCallback(() => setPlusHinweis(null), []);
    /**
     * „To-do" bzw. „Neues To-do anlegen …" im Plus (PR 4): der Anlegekasten
     * steht dann im Sheet statt am Seitenende, und nach dem Anlegen läuft die
     * gewählte Aktion mit der neuen Id weiter (`nachTodoAnlageRef`).
     */
    const [todoAnlageImSheet, setTodoAnlageImSheet] = useState(false);
    const nachTodoAnlageRef = React.useRef<((neueId: number, titel: string) => void) | null>(null);

    /*
     * DAS HANDY-LAYOUT (Plan „Vorgangsdetail mobil", PR 2) — nach BREITE,
     * also auch ein schmales PC-Fenster (Entscheidung 7 des Users). Das iPad
     * (769–1024) behält die Desktop-Anordnung.
     */
    const { isPhone } = useResponsive();
    const setTodoOffen = useCallback(
        (todoId: number, auf: boolean) => {
            // Auf dem Handy ist immer nur EIN To-do offen (Entscheidung 8).
            setOffeneTodos((vorher) => umschalteOffen(vorher, todoId, auf, { akkordeon: isPhone }));
            if (auf) merkeZuletztGeoeffnet(id, todoId);
        },
        [id, isPhone],
    );

    /*
     * Handy: Beim Öffnen des Vorgangs ist ein To-do aufgeklappt — das zuletzt
     * geöffnete, sonst das erste offene (Artifact: „standardmäßig das erste").
     * Einmal je Vorgang; wer danach alles zuklappt, behält das. Wird das
     * Fenster schmal, während mehrere offen sind, bleibt eines.
     */
    const startGesetztRef = React.useRef<number | null>(null);
    useEffect(() => {
        if (!isPhone) return;
        if (todos.length === 0) return;
        const zuletzt = ladeZuletztGeoeffnet(id);
        if (startGesetztRef.current !== id) {
            startGesetztRef.current = id;
            const start = startOffen(
                todos.map((t) => ({ id: t.id, erledigt: istTodoErledigt(t.status) })),
                zuletzt,
            );
            setOffeneTodos((vorher) => (vorher.size > 0 ? aufAkkordeon(vorher, zuletzt) : start != null ? new Set([start]) : vorher));
            return;
        }
        setOffeneTodos((vorher) => aufAkkordeon(vorher, zuletzt));
    }, [isPhone, todos, id]);

    useSprungmarke({
        suchParameter,
        setSuchParameter,
        bereit: !loading && verlaufGeladen && vorgang != null,
        notizen: notizenAnzeige,
        todoIds,
        zeigeHinweis,
        onAufklappen: (todoId) => setTodoOffen(todoId, true),
    });

    /* ── Rendern ─────────────────────────────────────────────────────────── */

    if (loading) {
        return (
            <div className="kvp-rd vg4-flaeche">
                <div className="vg4-page" style={{ alignItems: "center", paddingTop: 80 }}>
                    <Spinner label="Vorgang wird geladen…" />
                </div>
            </div>
        );
    }
    if (error || !vorgang) {
        return (
            <div className="kvp-rd vg4-flaeche">
                <div className="vg4-page">
                    <div className="vg4-fehler">{error ?? "Vorgang nicht gefunden."}</div>
                </div>
            </div>
        );
    }

    // Ab hier nie null: die frühen Returns oben decken `!vorgang` ab, und der
    // Hook baut `gemeinsameProps`, sobald ein Vorgang da ist.
    const strangProps = gemeinsameProps as GemeinsameStrangProps;

    /**
     * Die Sprechblasen an den Archivzeilen. Ohne To-do stehen Mail, Anruf und
     * Dokument NUR dort — ihre Kommentare bleiben aus dem Notizstrom heraus
     * (`versteckteKommentare` im Strang) und brauchen hier ihren Faden.
     */
    const archivFaeden = kommentareJeZiel(notizenAnzeige);
    const archivKommentare = {
        fadenZu: (ziel: KommentarZiel) => archivFaeden.get(zielSchluessel(ziel)) ?? [],
        users,
        ...(canEdit && currentUserId ? { aktuellerUserId: currentUserId } : {}),
        ...(canEdit && handlers.onKommentarNeu ? { onNeu: handlers.onKommentarNeu } : {}),
        ...(canEdit && handlers.onKommentarAendern ? { onAendern: handlers.onKommentarAendern } : {}),
        ...(canEdit && handlers.onKommentarLoeschen ? { onLoeschen: handlers.onKommentarLoeschen } : {}),
        // OHNE `canEdit`: abhaken darf, wer den Vorgang SIEHT (PO 20.09.2026:
        // „erstmal jeder") — gebeten wird der Erwähnte, und der ist nicht
        // zwangsläufig Bearbeiter. Das Backend prüft dasselbe.
        ...(handlers.onKommentarErledigt ? { onErledigt: handlers.onKommentarErledigt } : {}),
        // `#` und Anhang im Kommentar (PO 28.09.2026) — dasselbe Angebot wie
        // im Strang: was an diesem Vorgang hängt, ohne Personen.
        kandidaten: bezugKandidaten({
            mails: eintraege
                .filter((e) => e.mail)
                .map((e) => ({ id: e.mail!.id, subject: e.mail!.subject, from: e.mail!.senderName })),
            anrufe: eintraege
                .filter((e) => e.anruf)
                .map((e) => ({ id: e.anruf!.id, displayName: displayNameOf(e.anruf!), startedAt: e.anruf!.startedAt })),
            dokumente: dokumenteAus(eintraege).map((d) => ({ id: d.id, fileName: d.fileName })),
            risiken: bezugRisiken,
            vertraege: bezugVertraege,
        }),
        onBezugOeffnen: (art: NoteRefArt, refId: string) => {
            if (art === "mail") {
                const treffer = eintraege.find((e) => e.mail && String(e.mail.id) === refId);
                if (treffer?.mail) handlers.onOeffneMail(treffer.mail);
            } else if (art === "dokument") void documentService.openSignedDownload(Number(refId));
            else if (art === "risiko") navigate(`/risks/${refId}`);
            else if (art === "vertrag") navigate(`/contracts/${refId}`);
        },
        mitAnhang: true,
    };

    /**
     * Was das `⊕` braucht (13.09.2026). Im Vorgangskopf steht seit T-302
     * (29.10.2026) an seiner Stelle das A des Austauschs — das `⊕` trägt nur
     * noch die Vorgangs-Sonderkarte (`VorgangAlsTodoKarteV4`). Nachgeschlagen
     * wird weder dort noch in der Fußleiste, sondern im Archiv am Seitenende.
     */
    const aktionsOptionen: AktionsKnoepfeOptionen = {
        vorgangId: vorgang.id,
        contactId: vorgang.kunde?.id ?? null,
        contactName: vorgang.kunde ? kundeName(vorgang.kunde) : null,
        vorgangNbr: vorgang.vorgangNbr ?? null,
        vertragNr: vorgang.vertrag?.contractNbr ?? null,
        risikoName: vorgang.risiko?.name ?? null,
        zugewieseneMessageIds,
        vorhandeneDokumentIds,
        onGeaendert: handlers.onGeaendert,
    };

    const todoAnlegenKnopf = canEdit && neuesTodo == null && (
        <button
            type="button"
            className="vg3-btn primary"
            onClick={() => setNeuesTodo(leererTodoEntwurf(slotsFromDto(vorgang), {
                hauptverantwortlich: vorgang.hauptverantwortlich,
                bearbeiter: vorgang.bearbeiter,
                zuInformierende: vorgang.zuInformierende,
                currentUserId,
            }))}
        >
            <IcoPlus size={14} /> {hatTodos ? "Weiteres To-do hinzufügen" : "To-do hinzufügen"}
        </button>
    );

    const todoKasten = (imSheet: boolean) => canEdit && neuesTodo != null && (
        <TodoAnlegenKasten
            entwurf={neuesTodo}
            onChange={setNeuesTodo}
            users={users}
            persoenlich={vorgang.persoenlich === true}
            busy={todoBusy}
            onAnlegen={() => void todoAnlegen()}
            onAbbrechen={() => {
                // Das gemerkte Ziel MIT wegwerfen: sonst zöge das nächste,
                // ganz anders gemeinte To-do den hier abgebrochenen Eintrag
                // still zu sich herüber.
                setNeuesTodo(null);
                setTodoZielEintrag(null);
                setErstesTodoTitel(null);
                if (imSheet) {
                    nachTodoAnlageRef.current = null;
                    setTodoAnlageImSheet(false);
                    setPlusAnfrage(null);
                }
            }}
            erstesTodo={
                hatTodos
                    ? undefined
                    : (() => {
                          const vorschlag = titel.trim() || betreffOhneArt(vorgang.vorgangsart, vorgang.titel);
                          return {
                              titel: erstesTodoTitel ?? vorschlag,
                              vorschlag,
                              onChange: setErstesTodoTitel,
                          };
                      })()
            }
            onAuswaehlen={vorgang.persoenlich === true || imSheet ? undefined : () => setVerknuepfenOffen(true)}
        />
    );
    const todoAnlegenFeld = !todoAnlageImSheet && todoKasten(false);

    /**
     * „Weiteres To-do hinzufügen“ steht MIT To-dos am Ende der Liste
     * (PO 13.09.2026, Marker 16) — „dort, wo man nach dem Lesen ankommt, nicht
     * vor der Arbeit“. OHNE To-dos bleibt es im Fuß der Vorgangskarte, wo es
     * der nächste Schritt ist. Beide Stellen rendern DIESELBEN Knoten, damit
     * der Anlege-Kasten dort aufgeht, wo der Knopf stand.
     */
    const anlegeBlock = (todoAnlegenKnopf || todoAnlegenFeld) && (
        <div className="vg4-todo-anlegen">
            {todoAnlegenKnopf}
            {todoAnlegenFeld}
        </div>
    );

    const archiv = (
        <>
            {/* Das Archiv ganz unten, UNTER allen To-dos (PO 11.09.2026,
                17:43). Es bekommt `eintraege` — nicht `eigeneEintraege` —
                und die Notizen: „da ist alles von allen To-dos drin.“ */}
            <ArchivAbschnitt
                vorgangId={vorgang.id}
                vorgangNbr={vorgang.vorgangNbr ?? null}
                eintraege={eintraege}
                notizen={notizenAnzeige}
                todos={archivTodos}
                kundeName={vorgang.kunde ? kundeName(vorgang.kunde) : null}
                onOeffneMail={handlers.onOeffneMail}
                onOeffneTermin={(terminId) => navigate(`/termine/notizen/${terminId}`)}
                // Die Termin-Notizen stehen als Karten im Strom (PO
                // 14.09.2026) — und damit auch hier, als eigene Art mit
                // Chip.
                mitTerminen
                // „Nur dann muss ich nach dem Ding auch suchen können"
                // (PO 11.09.2026). Seit die Kommentare hinter der
                // Sprechblase stehen, MÜSSEN sie im Archiv auffindbar
                // sein — als eigene Art, mit ihrem Bezug in der Zeile.
                mitKommentaren
                // NOTIZEN NICHT IM ARCHIV (PO 28.09.2026) — sie stehen
                // als fortlaufender Text in ihrer Karte; im Archiv stand
                // jede ein zweites Mal.
                ohneNotizen
                // DAS ARCHIV BLEIBT ZU — immer (PO 18.09.2026: „Archiv
                // bleibt bei Vorgang ohne und mit To-dos eingeklappt").
                // Seit der Verlauf in JEDER Karte steht, ist das Archiv
                // nicht mehr der zweite Leseort, sondern nur noch die
                // Suche über alles.
                mitHerkunft={hatTodos}
                {...(handlers.onEreignisMenue ? { onEreignisMenue: handlers.onEreignisMenue } : {})}
                kommentare={archivKommentare}
            />
        </>
    );

    const verknuepfteKarten = (
        <>
            {/* To-dos anderer Vorgänge stehen NACH den eigenen: die Arbeit
                dieses Vorgangs kommt zuerst. */}
            {verknuepfte.map((t) => (
                <section key={`v-${t.id}`} className="vg4-card">
                    <VerknuepftesTodoKarte
                        todo={t}
                        canEdit={canEdit}
                        /* Der „Gehört zu"-Verweis führt in die Vorgangsansicht.
                           Ausdrücklich gesetzt, obwohl es inzwischen auch der
                           Vorgabewert ist: die Stelle soll lesbar bleiben. */
                        zielBasis="/vorgaenge"
                        onLoesen={async (ziel) => {
                            const ok = await bestaetigen({
                                titel: "Verknüpfung lösen?",
                                text: `Die Verknüpfung zu „${ziel.titel}“ wird gelöst. Das To-do selbst bleibt bestehen.`,
                                ja: "Lösen",
                            });
                            if (!ok) return;
                            try {
                                await vorgangService.loeseTodoVerknuepfung(id, ziel.id);
                                setVerknuepfte((liste) => liste.filter((x) => x.id !== ziel.id));
                            } catch (err: any) {
                                console.error("[VorgangDetailV4] Verknüpfung lösen fehlgeschlagen", err);
                                void hinweisen({
                                    titel: "Verknüpfung nicht gelöst",
                                    text: err?.message ?? "Verknüpfung konnte nicht gelöst werden.",
                                    ton: "danger",
                                });
                            }
                        }}
                    />
                </section>
            ))}
        </>
    );

    const vorgangsKarteNode = (
        <>
            {vorgangsKarte && (
                <VorgangAlsTodoKarteV4
                    {...handlers}
                    {...strangProps}
                    // Die laufende Notiz steht oben im Vorgang, nicht hier.
                    alleNotizen={ohneLaufende}
                    vorgang={vorgang}
                    titel={titel}
                    onTitel={setTitel}
                    eintraege={eigeneEintraege}
                    onPatch={(data) => void patch(data)}
                    onPatchBeteiligte={(feld, next) => void patchBeteiligte(feld, next)}
                    onAnwendenLinks={(r: CascadeResult) => void patch(vorgangBodyAusPatch(r.patch))}
                    aktionsOptionen={aktionsOptionen}
                    entwuerfe={entwuerfe === null ? null : entwurfsOrte.amVorgang}
                />
            )}
        </>
    );

    const seitenDialoge = (
        <>
            {zusDialog && (
                <ZusammenfassungDialog
                    betreff={vorgang.titel || vorgang.vorgangNbr || `Vorgang ${vorgang.id}`}
                    wert={vorgang.zusammenfassung ?? ""}
                    modus={zusDialog}
                    onSpeichern={(text) => patch({ zusammenfassung: text || null })}
                    onSchliessen={() => setZusDialog(null)}
                />
            )}

            {verknuepfenOffen && (
                <TodoVerknuepfenDialog
                    vorgangId={vorgang.id}
                    contactId={vorgang.kunde?.id ?? null}
                    onClose={() => setVerknuepfenOffen(false)}
                    onVerknuepft={ladeVerknuepfte}
                />
            )}

            {/* Anders als das Verknüpfen oben wechselt hier die HEIMAT des
                To-dos. Danach muss der ganze Vorgang neu geladen werden,
                nicht nur die Verknüpfungsliste: Das To-do ist neu da, sein
                Schriftwechsel auch, und der Vorgang hat ab jetzt To-dos —
                die halbe Seite sieht anders aus. */}
            {todoVerschieben && (
                <TodoVerschiebenDialog
                    vorgangId={vorgang.id}
                    contactId={vorgang.kunde?.id ?? null}
                    onClose={() => setTodoVerschieben(false)}
                    onVerschoben={async () => {
                        await ladeVorgang();
                        await ladeVerknuepfte();
                    }}
                />
            )}

            {/* Hier NICHT neu laden: Dieser Vorgang liegt danach im
                Papierkorb. Ein `ladeVorgang()` liefe in den 404 und die
                Seite stünde als Fehler da, obwohl alles geklappt hat.
                Stattdessen dorthin, wo die Arbeit jetzt steht — der
                Zielvorgang, mit dem neuen To-do. */}
            {vorgangVerschieben && (
                <VorgangAlsTodoVerschiebenDialog
                    vorgangId={vorgang.id}
                    titel={vorgang.titel ?? ""}
                    contactId={vorgang.kunde?.id ?? null}
                    onClose={() => setVorgangVerschieben(false)}
                    onVerschoben={(zielVorgangId) => {
                        navigate(`/vorgaenge/${zielVorgangId}`, { replace: true });
                    }}
                />
            )}

            {/* Derselbe Dialog wie in der Liste, nach der Wahl des zweiten
                Vorgangs. Verschwindet DIESER Vorgang, geht die Seite zum
                Überlebenden — ein Neuladen liefe in den 404. */}
            {zusammenfuehrenMit && (
                <VorgangZusammenfuehrenMitDialog
                    vorgang={{
                        id: vorgang.id,
                        titel: vorgang.titel ?? "",
                        vorgangNbr: vorgang.vorgangNbr ?? null,
                        createdAt: vorgang.createdAt ?? null,
                    }}
                    contactId={vorgang.kunde?.id ?? null}
                    onClose={() => setZusammenfuehrenMit(false)}
                    onFertig={(zielId, verschwundene) => {
                        if (verschwundene.includes(vorgang.id)) {
                            navigate(`/vorgaenge/${zielId}`, { replace: true });
                            return;
                        }
                        void ladeAlles();
                        zeigeHinweis(
                            zielId === vorgang.id
                                ? "Zusammengeführt — alles steht jetzt in diesem Vorgang."
                                : "Die ausgewählten To-dos stehen jetzt im anderen Vorgang.",
                        );
                    }}
                />
            )}

            {teilen && (
                <VorgangTeilenDialog
                    vorgangId={vorgang.id}
                    titel={vorgang.titel ?? ""}
                    todos={todos}
                    onClose={() => setTeilen(false)}
                    onGeteilt={async (_neueId, neuerTitel) => {
                        await ladeAlles();
                        zeigeHinweis(`„${neuerTitel}“ ist jetzt ein eigener Vorgang.`);
                    }}
                />
            )}

            {/* Rechtsklick-Menü, Anruf-Zuordnen, Umbenennen und die
                Hinweiszeile — kommen aus dem Strang (`useVorgangStrang`). */}
            {strang.overlays}
        </>
    );

    /** Die Zählung je To-do für die kompakte Zeile auf dem Handy. */
    const zaehlungJeTodo = new Map(todoZaehlungen.map((z) => [z.todo.id, z.zaehlung]));
    const offeneAnzahl = todos.filter((t) => !istTodoErledigt(t.status)).length;
    const offenesTodoId = todos.find((t) => offeneTodos.has(t.id))?.id ?? null;
    const vorgangsName = titel || vorgang.vorgangNbr || `Vorgang ${vorgang.id}`;
    const todoName = (todoId: number | null) =>
        todoId == null ? vorgangsName : (todos.find((t) => t.id === todoId)?.titel || `To-do ${todoId}`);
    // Das Plus wählt sein Ziel selbst — die Optionen tragen kein `todoId`.
    const plusOptionen: AktionsKnoepfeOptionen = { ...aktionsOptionen, todoId: undefined };
    const plusTodos = todos.map((t) => ({
        id: t.id,
        titel: t.titel ?? "",
        erledigt: istTodoErledigt(t.status),
        frist: t.frist ?? null,
        twv: t.twv ?? null,
    }));
    const plusTodoAnlage: PlusTodoAnlage = {
        starte: (nachAnlage) => {
            nachTodoAnlageRef.current = nachAnlage;
            setTodoAnlageImSheet(true);
            setTodoZielEintrag(null);
            setNeuesTodo(
                leererTodoEntwurf(slotsFromDto(vorgang), {
                    hauptverantwortlich: vorgang.hauptverantwortlich,
                    bearbeiter: vorgang.bearbeiter,
                    zuInformierende: vorgang.zuInformierende,
                    currentUserId,
                }),
            );
        },
        abbrechen: () => {
            nachTodoAnlageRef.current = null;
            setTodoAnlageImSheet(false);
            setNeuesTodo(null);
            setErstesTodoTitel(null);
        },
        kasten: todoAnlageImSheet ? todoKasten(true) || null : null,
    };

    /**
     * NEUE NOTIZ AUS DEM PLUS (Plan mobil, PR 5). Anhänge lädt erst das
     * Abschicken hoch (wie beim Kommentar); Klartext wird zu Absätzen. Die
     * laufende Notiz des Vorgangs schreibt über `laufendeHandlers` (`laufend`).
     */
    const plusNotizAnlegen = async ({ text, mentions, zusatz, ziel }: PlusNotiz) => {
        try {
            const inhalt = await baueKommentarInhalt(text, zusatz, (datei) =>
                strang.anhangHochladen(datei, ziel.laufend ? null : ziel.todoId),
            );
            const html = inhalt.format === "html" ? inhalt.text : textAlsNotizHtml(inhalt.text);
            const wege = ziel.laufend ? laufendeHandlers : handlers;
            await wege.onNotizNeu(
                { text: html, mentions, zuInformieren: [] },
                ziel.todoId ?? undefined,
                "html",
                inhalt.refs,
            );
        } catch (err: any) {
            // Die Eingabe zeigt den Fehler selbst und behält den Text.
            console.error("[VorgangDetailV4] Notiz aus dem Plus anlegen fehlgeschlagen", err);
            throw err;
        }
    };

    /*
     * ══ DAS HANDY-LAYOUT (Plan „Vorgangsdetail mobil", PR 2) ═══════════════
     * Eine Seite, ein Fachmodell: dieselben Daten, Handler und Dialoge wie am
     * Desktop, nur anders angeordnet (Artifact, Artboard 1):
     *
     *     Kopf (Art, Betreff, Kontakt mit ☎/✉, „Details")
     *     [ohne To-dos]  Notizen + Verlauf des Vorgangs
     *     [mit To-dos]   Notizen zum Vorgang · „To-dos  3 offen" · Akkordeon
     *     verknüpfte To-dos · Archiv (zu)
     *
     * Ziehen und Dateiablage entfallen (Touch); die Wege stehen in den Menüs.
     * ═══════════════════════════════════════════════════════════════════════
     */
    if (isPhone) {
        return (
            <div className="kvp-rd vg4-flaeche vg4-mobil">
                <div className="vg4-page">
                    <section className="vg4-card vg4m-kopf">
                        <VorgangKopfV4
                            mobil
                            kontaktKnoepfe={
                                <KopfKontaktKnoepfe
                                    optionen={aktionsOptionen}
                                    canEdit={canEdit}
                                    hatTodos={hatTodos}
                                    offenesTodoId={offenesTodoId}
                                    onMailMitTodos={() => setPlusAnfrage({ aktion: "mail" })}
                                />
                            }
                            vorgang={vorgang}
                            titel={titel}
                            onTitel={setTitel}
                            beschreibung={beschreibung}
                            onBeschreibung={setBeschreibung}
                            users={users}
                            canEdit={canEdit}
                            hatTodos={hatTodos}
                            ersteller={ersteller}
                            zuletzt={zuletzt}
                            savedFlash={savedFlash}
                            saveError={saveError}
                            onPatch={(data) => void patch(data)}
                            onPatchBeteiligte={(feld, next) => void patchBeteiligte(feld, next)}
                            onAnwendenLinks={(r: CascadeResult) => void patch(vorgangBodyAusPatch(r.patch))}
                            austausch={austausch}
                            onAustauschGeaendert={ladeAustausch}
                            onTodoVerschieben={canEdit && !hatTodos ? () => setTodoVerschieben(true) : undefined}
                            onVorgangVerschieben={canEdit && !hatTodos ? () => setVorgangVerschieben(true) : undefined}
                            onZusammenfuehren={canEdit ? () => setZusammenfuehrenMit(true) : undefined}
                            onTeilen={canEdit && todos.length >= 2 ? () => setTeilen(true) : undefined}
                        />
                    </section>

                    <ZusammenfassungKarte
                        text={vorgang.zusammenfassung}
                        canEdit={canEdit}
                        onAendern={() => setZusDialog("aendern")}
                    />

                    {!hatTodos && (
                        <section className="vg4-card vg4m-strang">
                            <ArbeitsstrangV4
                                {...handlers}
                                {...strangProps}
                                eintraege={eigeneEintraege}
                                entwuerfe={entwuerfe === null ? null : entwurfsOrte.amVorgang}
                                mitVerlauf
                                ohneAktionsleiste={canEdit}
                                kompakt
                            />
                            {anlegeBlock && (
                                <>
                                    <div className="vg4-trenner" role="presentation" />
                                    {anlegeBlock}
                                </>
                            )}
                        </section>
                    )}

                    {hatTodos && (canEdit || laufende.length > 0) && (
                        <section className="vg4-card vg4m-strang vg4m-laufende-notiz">
                            <ArbeitsstrangV4
                                {...laufendeHandlers}
                                {...strangProps}
                                alleNotizen={laufende}
                                eintraege={KEINE_EINTRAEGE}
                                nurNotizen
                                notizTitel="Notizen zum Vorgang"
                            />
                        </section>
                    )}

                    {hatTodos && !vorgangsKarte && (
                        <VorgangEntwuerfeV4 entwuerfe={entwurfsOrte.amVorgang} onGesendet={handlers.onGeaendert} />
                    )}

                    {hatTodos && (
                        <div className="vg4m-todos-kopf">
                            <h2>To-dos</h2>
                            <span>{offeneAnzahl === 1 ? "1 offen" : `${offeneAnzahl} offen`}</span>
                        </div>
                    )}

                    {vorgangsKarteNode}

                    {todos.map((t) => (
                        <TodoKarteV4
                            key={t.id}
                            {...handlers}
                            {...strangProps}
                            mobil
                            zaehlung={zaehlungJeTodo.get(t.id) ?? ""}
                            todo={t}
                            eintraege={verlaufFuerTodo(eintraege, t.id)}
                            entwuerfe={entwuerfe === null ? null : (entwurfsOrte.jeTodo.get(t.id) ?? KEINE_ENTWUERFE)}
                            andereTodos={todoZaehlungen}
                            austausch={austausch}
                            onAustauschGeaendert={ladeAustausch}
                            offen={offeneTodos.has(t.id)}
                            onOffen={(auf) => setTodoOffen(t.id, auf)}
                            onHinzufuegen={() =>
                                setPlusAnfrage({ festesZiel: { todoId: t.id, name: todoName(t.id) } })
                            }
                        />
                    ))}

                    {hatTodos && anlegeBlock}
                    {verknuepfteKarten}
                    {archiv}
                    {seitenDialoge}

                    {/* DAS PLUS (Plan mobil, PR 3/4) — ohne To-dos mit dem Vorgang
                        als Ziel, mit To-dos fragt es nach dem To-do. */}
                    {canEdit && <PlusKnopf onClick={() => setPlusAnfrage({})} />}
                    {canEdit && (
                        <PlusSheet
                            anfrage={plusAnfrage}
                            onSchliessen={plusSchliessen}
                            optionen={plusOptionen}
                            standardZiel={{ todoId: null, name: vorgangsName }}
                            zielName={todoName}
                            hinweis={setPlusHinweis}
                            todos={plusTodos}
                            offen={offeneTodos}
                            zuletzt={ladeZuletztGeoeffnet(vorgang.id)}
                            todoAnlage={plusTodoAnlage}
                            notizAnlegen={plusNotizAnlegen}
                            laufendeNotiz={hatTodos}
                            users={users}
                            austausch={{ liste: austausch, canEdit, onGeaendert: ladeAustausch }}
                        />
                    )}
                    <PlusHinweis text={plusHinweis} onSchliessen={plusHinweisWeg} />
                </div>
            </div>
        );
    }

    return (
        /* `vg4-flaeche` tönt den Grund unter den Karten (#f4eff3). Ohne ihn
           stünde Weiß auf Weiß und die Karten hätten keine Kante. Als eigene
           Klasse, nicht über `--bg` — das Token trägt die ganze Redesign-App. */
        <div className="kvp-rd vg4-flaeche">
            <div className="vg4-page">
                {/* Mit To-dos trägt die Karte nur noch die Kontaktzeile — die
                    eigene Klasse nimmt ihr das Polster einer vollen Karte.
                    Nur dann: bleibt der Betreff im Kopf, weil ihn kein To-do
                    trägt, sind es zwei Zeilen, und die Karte braucht ihr
                    Polster samt Haarlinie unter der Kontaktzeile. */}
                <DateiDropZoneV4
                    className="vg4-card"
                    vorgangId={vorgang.id}
                    zielName={titel || vorgang.vorgangNbr || `Vorgang ${vorgang.id}`}
                    canEdit={canEdit}
                    onGeaendert={handlers.onGeaendert}
                >
                    <VorgangKopfV4
                        vorgang={vorgang}
                        titel={titel}
                        onTitel={setTitel}
                        beschreibung={beschreibung}
                        onBeschreibung={setBeschreibung}
                        users={users}
                        canEdit={canEdit}
                        hatTodos={hatTodos}
                        ersteller={ersteller}
                        zuletzt={zuletzt}
                        savedFlash={savedFlash}
                        saveError={saveError}
                        onPatch={(data) => void patch(data)}
                        onPatchBeteiligte={(feld, next) => void patchBeteiligte(feld, next)}
                        onAnwendenLinks={(r: CascadeResult) => void patch(vorgangBodyAusPatch(r.patch))}
                        austausch={austausch}
                        onAustauschGeaendert={ladeAustausch}
                        /* Beide ⋯-Wege nur ohne To-dos (PO 21.09.2026) — die
                           Reihe, in der das Menü sitzt, steht ohnehin nur dann.
                           „Als To-do verschieben" ergibt auch nur dann einen
                           Sinn: Mit To-dos IST der Vorgang schon mehr als ein
                           einzelner Arbeitsschritt. */
                        onTodoVerschieben={
                            canEdit && !hatTodos ? () => setTodoVerschieben(true) : undefined
                        }
                        onVorgangVerschieben={
                            canEdit && !hatTodos ? () => setVorgangVerschieben(true) : undefined
                        }
                        /* Mit UND ohne To-dos (PO 28.09.2026). Teilen erst ab
                           zwei To-dos — mit einem gibt es „Als eigenen Vorgang"
                           am To-do, und einer muss hier bleiben. */
                        onZusammenfuehren={canEdit ? () => setZusammenfuehrenMit(true) : undefined}
                        onTeilen={canEdit && todos.length >= 2 ? () => setTeilen(true) : undefined}
                    />

                    {/* Die Zusammenfassung steht ÜBER der Arbeit (PO 09.09.2026):
                        Wer eine abgeschlossene Akte öffnet, will zuerst wissen,
                        was herauskam. Ohne Text erscheint sie gar nicht. */}
                    {!hatTodos && (
                        <ZusammenfassungKarte
                            text={vorgang.zusammenfassung}
                            canEdit={canEdit}
                            onAendern={() => setZusDialog("aendern")}
                        />
                    )}

                    {/* OHNE To-dos trägt die Vorgangskarte den Notizstrom
                        selbst; mit To-dos trägt ihn jedes To-do. */}
                    {!hatTodos && (
                        /*
                         * MIT VERLAUF — auch ohne To-do (PO 18.09.2026: „wenn
                         * ein Vorgang kein To-do hat, dann Verlauf unter den
                         * Notizen dennoch anzeigen").
                         *
                         * Vom 15. bis 18.09.2026 stand er hier nicht: Das
                         * aufgeklappte Archiv unter der Karte war sein Ersatz.
                         * Das hieß aber, dass derselbe Verlauf je nach Zustand
                         * des Vorgangs an zwei verschiedenen Orten stand — und
                         * beim ersten To-do sprang er von unten in die Karte.
                         */
                        <ArbeitsstrangV4
                            {...handlers}
                            {...strangProps}
                            eintraege={eigeneEintraege}
                            entwuerfe={entwuerfe === null ? null : entwurfsOrte.amVorgang}
                            mitVerlauf
                        />
                    )}

                    {/* MIT To-dos: die laufende Notiz des Vorgangs — nur Text,
                        kein Verlauf, keine Aktionsleiste. Ohne Schreibrecht
                        und ohne Inhalt bleibt sie weg. */}
                    {hatTodos && (canEdit || laufende.length > 0) && (
                        <div className="vg4-laufende-notiz">
                            <ArbeitsstrangV4
                                {...laufendeHandlers}
                                {...strangProps}
                                alleNotizen={laufende}
                                eintraege={KEINE_EINTRAEGE}
                                nurNotizen
                                notizTitel="Notizen zum Vorgang"
                            />
                        </div>
                    )}

                    {/* Der Fuß der Karte: „To-do hinzufügen“ unter der
                        Aktionsleiste, durch einen zweiten Trenner abgesetzt —
                        nur OHNE To-dos. Der Trenner setzt ihn INNERHALB der
                        Karte von der Aktionsleiste ab; am neuen Ort unten steht
                        der Block frei zwischen den Karten und braucht ihn
                        nicht (Mockup `.anlegen-zeile`). */}
                    {!hatTodos && anlegeBlock && (
                        <>
                            <div className="vg4-trenner" role="presentation" />
                            {anlegeBlock}
                        </>
                    )}
                </DateiDropZoneV4>

                {/* T-231: Mail-Entwürfe stehen als Karten im Verlauf ihres
                    Strangs (User 22.10.2026). Nur Entwürfe am Vorgang selbst,
                    wenn er To-dos, aber keine eigene Karte hat, stehen hier in
                    der kleinen Liste — sonst gingen sie verloren. */}
                {hatTodos && !vorgangsKarte && (
                    <VorgangEntwuerfeV4 entwuerfe={entwurfsOrte.amVorgang} onGesendet={handlers.onGeaendert} />
                )}

                {hatTodos && (
                    <ZusammenfassungKarte
                        text={vorgang.zusammenfassung}
                        canEdit={canEdit}
                        onAendern={() => setZusDialog("aendern")}
                    />
                )}

                {/* DER VORGANG ALS ERSTE TO-DO-KARTE (PO 13./14.09.2026):
                    „vorgang mit todos wo notizen und mails nicht zu todo dann
                    bitte rendern als vorgang gleich erstes todo."

                    Sie steht VOR den To-dos — der Vorgang ist das erste. Und
                    sie erscheint nur, wenn er auch etwas zu zeigen hat; sonst
                    wüchse jedem Vorgang eine leere Karte über seine Arbeit
                    (`zeigeVorgangsStrang`). */}
                {vorgangsKarteNode}

                {todos.map((t, i) => (
                    <TodoKarteV4
                        key={t.id}
                        {...handlers}
                        {...strangProps}
                        todo={t}
                        eintraege={verlaufFuerTodo(eintraege, t.id)}
                        entwuerfe={entwuerfe === null ? null : (entwurfsOrte.jeTodo.get(t.id) ?? KEINE_ENTWUERFE)}
                        andereTodos={todoZaehlungen}
                        austausch={austausch}
                        onAustauschGeaendert={ladeAustausch}
                        offen={offeneTodos.has(t.id)}
                        onOffen={(auf) => setTodoOffen(t.id, auf)}
                    />
                ))}

                {/* Ans ENDE der eigenen To-dos, vor die verknüpften und vor das
                    Archiv (PO 13.09.2026, Marker 16). */}
                {hatTodos && anlegeBlock}

                {verknuepfteKarten}

                {archiv}

                {seitenDialoge}
            </div>
        </div>
    );
};

export default VorgangDetailPageV4;
